/**
 * Training Jobs List Page
 */

import {
  Button,
  Card,
  CardBody,
  Chip,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow,
} from '@heroui/react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Layers } from 'lucide-react';

import { useTrainingJobs } from '@/lib/api/hooks/training';
import { formatRelativeTime } from '@/lib/design-system';

export const Route = createFileRoute('/training/')({
  component: TrainingJobsPage,
});

function getStatusColor(status: string) {
  switch (status) {
    case 'completed':
      return 'success';
    case 'training':
    case 'pending':
      return 'primary';
    case 'failed':
      return 'danger';
    case 'cancelled':
      return 'default';
    default:
      return 'default';
  }
}

function TrainingJobsPage() {
  const { data: jobs, isLoading, error } = useTrainingJobs();

  return (
    <div className="container-page py-4">
      <div className="mb-4 flex items-center justify-end">
        <Button
          as={Link}
          to="/training/new"
          color="primary"
          size="sm"
        >
          New Training Job
        </Button>
      </div>

      {isLoading && (
        <div className="flex justify-center py-8">
          <Spinner size="md" />
        </div>
      )}

      {error && (
        <Card className="border border-default-100 shadow-none">
          <CardBody className="text-center text-xs text-danger">
            Failed to load training jobs. Please try again.
          </CardBody>
        </Card>
      )}

      {!isLoading && !error && jobs && jobs.length === 0 && (
        <Card className="border border-default-100 shadow-none">
          <CardBody className="flex flex-col items-center gap-3 py-8 text-center">
            <div className="rounded-full bg-default-100 p-3">
              <Layers className="h-5 w-5 text-default-500" />
            </div>
            <p className="text-xs text-default-500">
              No training jobs yet
            </p>
            <Button
              as={Link}
              to="/training/new"
              color="primary"
              size="sm"
            >
              New Training Job
            </Button>
          </CardBody>
        </Card>
      )}

      {!isLoading && !error && jobs && jobs.length > 0 && (
        <Table
          aria-label="Training jobs table"
          selectionMode="none"
          classNames={{
            wrapper: 'shadow-none border border-default-100',
            th: 'text-xs font-medium text-default-500 bg-default-50',
            td: 'text-xs py-2',
          }}
        >
          <TableHeader>
            <TableColumn>JOB ID</TableColumn>
            <TableColumn>DATASET</TableColumn>
            <TableColumn>STATUS</TableColumn>
            <TableColumn>CREATED</TableColumn>
          </TableHeader>
          <TableBody>
            {jobs.map((job) => (
              <TableRow
                key={job.id}
                className="cursor-pointer transition-colors hover:bg-default-50"
                onClick={() => {
                  window.location.href = `/training/${job.id}`;
                }}
              >
                <TableCell>
                  <p className="font-medium font-mono text-[10px]">{job.id.slice(0, 8)}</p>
                </TableCell>
                <TableCell className="text-default-500 text-[10px]">
                  {job.dataset_id?.slice(0, 8)}...
                </TableCell>
                <TableCell>
                  <Chip size="sm" color={getStatusColor(job.status)} variant="flat" classNames={{ content: 'text-[10px]' }}>
                    {job.status}
                  </Chip>
                </TableCell>
                <TableCell className="text-default-500">
                  {formatRelativeTime(new Date(job.created_at))}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
